import { ReactNode } from 'react';

import {
  Column,
  Icons,
  useSwipeableViews,
  Box,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';

import { ClusterCell } from './ClusterCell';

import {
  isAnywhereCluster,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';

import { NoData, NoResults, SearchableTable } from '@cast/shared-search-ui';

type Props = {
  onSelect: (cluster: Cluster) => void;
  disableAnywhereSelection?: boolean;
  disabledAnywhereTooltip?: ReactNode;
  disableKentManagedClusterSelection?: boolean;
  disabledKentManagedClusterTooltip?: ReactNode;
};

export const ClustersTable = ({
  onSelect,
  disableAnywhereSelection = false,
  disabledAnywhereTooltip,
  disableKentManagedClusterSelection = true,
  disabledKentManagedClusterTooltip = 'Clusters with Karpenter autoscaler are not supported',
}: Props) => {
  const swipeableViewsCtx = useSwipeableViews();

  const isClusterAnywhereDisabled = (cluster: Cluster) =>
    disableAnywhereSelection && isAnywhereCluster(cluster);

  const isKentManagedClusterDisabled = (cluster: Cluster) =>
    disableKentManagedClusterSelection && isKentManagedCluster(cluster);

  const isSelectionDisabled = (cluster: Cluster) =>
    isClusterAnywhereDisabled(cluster) || isKentManagedClusterDisabled(cluster);

  return (
    <SearchableTable<Cluster>
      testId="select-cluster-table"
      maxHeight="240px"
      components={{
        noData: (
          <NoData
            icon={<Icons.Nut />}
            entity="active cluster"
            sx={{ my: 24 }}
          />
        ),
        noResults: <NoResults entity="active cluster" sx={{ my: 24 }} />,
      }}
      getRowTestId={({ name }) => `cluster-row-${name}`}
      onRowClick={(_, { row }) => {
        if (isSelectionDisabled(row)) {
          return;
        }
        onSelect(row);
        swipeableViewsCtx?.goToNextSlide();
      }}
      sx={{
        '.DS-Table-BodyRowWrapper': {
          borderColor: `${tokens.theme.border.structural.neutral} !important`,
        },
        '.DS-Table-CellWrapper': {
          backgroundColor: tokens.theme.bg.structural.surface,
          padding: '8px 0',
        },
        '.DS-Table-BodyRow:hover .DS-Table-CellWrapper': {
          backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
        },
        '.DS-Table-TopPeripheralsWrapper': {
          display: 'none',
        },
        // Should be removed when UI-3514 is implemented
        '.DS-Table-lastRow .DS-Table-BodyRow .DS-Table-CellWrapper.DS-Table-BodyCellWrapper':
          {
            paddingBottom: '8px',
          },
      }}
    >
      <Column<Cluster>
        id="name"
        header="Name"
        minWidth={140}
        cellWrapperSx={{ pl: 24 }}
        renderCell={(cluster) => (
          <ClusterCell
            cluster={cluster}
            anywhereDisabled={disableAnywhereSelection}
            anywhereDisabledTooltip={disabledAnywhereTooltip}
            kentManagedDisabled={disableKentManagedClusterSelection}
            kentManagedDisabledTooltip={disabledKentManagedClusterTooltip}
          />
        )}
        sortable
      />
      <Column<Cluster>
        id="actions"
        width={62}
        cellWrapperSx={{ pr: 24 }}
        renderCell={(cluster) => {
          const isDisabled = isSelectionDisabled(cluster);

          return (
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <Icons.CaretRight
                size={20}
                color={
                  isDisabled ? tokens.theme.fg.structural.secondary : undefined
                }
              />
            </Box>
          );
        }}
      />
    </SearchableTable>
  );
};
